import React, { useEffect, useMemo, useState } from "react";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Loader from "../../components/ui/Loader";
import { useGetDashboardQuery } from "../../store/api/analyticsApi";
import type { ChangeType, IconName } from "../../types";
import { arrayToCsv, downloadCsv } from "../../utils/csvExport";
import { formatCurrency } from "../../utils/formatCurrency";
import KPICard from "./components/KPICard";
import MaintenanceChart from "./components/MaintenanceChart";
import PaymentSummary from "./components/PaymentSummary";
import PropertySelector from "./components/PropertySelector";
import QuickActions from "./components/QuickActions";
import RecentActivities from "./components/RecentActivities";

interface KPIDataItem {
	title: string;
	value: string;
	change: string;
	changeType: ChangeType;
	icon: IconName;
	color: "primary" | "secondary" | "success" | "warning" | "error";
}

const AdminDashboard: React.FC = () => {
	const [selectedProperty, setSelectedProperty] = useState("all");
	const [dateRange, setDateRange] = useState("30days");
	const [currentTime, setCurrentTime] = useState(new Date());

	// Fetch dashboard data
	const {
		data: dashboardData,
		isLoading: isLoadingDashboard,
		error: dashboardError,
	} = useGetDashboardQuery();

	// Update current time every minute
	useEffect(() => {
		const timer = setInterval(() => {
			setCurrentTime(new Date());
		}, 60000);

		return () => clearInterval(timer);
	}, []);

	// Calculate KPI data from real API data
	const kpiData: KPIDataItem[] = useMemo(() => {
		// Default fallback values
		const defaultKPIs: KPIDataItem[] = [
			{
				title: "Occupancy Rate",
				value: "0%",
				change: "0%",
				changeType: "increase",
				icon: "Building2",
				color: "primary",
			},
			{
				title: "Monthly Revenue",
				value: "₦0",
				change: "0%",
				changeType: "increase",
				icon: "Currency",
				color: "success",
			},
			{
				title: "Pending Maintenance",
				value: "0",
				change: "0",
				changeType: "decrease",
				icon: "Wrench",
				color: "warning",
			},
			{
				title: "Total Properties",
				value: "0",
				change: "0 occupied",
				changeType: "increase",
				icon: "Calendar",
				color: "error",
			},
		];

		if (!dashboardData) {
			return defaultKPIs;
		}

		try {
			// Safely access nested properties with fallbacks
			const properties = dashboardData.properties || {};
			const payments = dashboardData.payments || {};
			const maintenance = dashboardData.maintenance || {};

			const occupancyRate = Number(properties.occupancy_rate) || 0;
			const monthlyRevenue = Number(payments.monthly_revenue) || 0;
			const pendingMaintenance = Number(maintenance.pending) || 0;
			const totalProperties = Number(properties.total) || 0;
			const occupiedProperties = Number(properties.occupied) || 0;

			return [
				{
					title: "Occupancy Rate",
					value: `${occupancyRate.toFixed(1)}%`,
					change: "0%", // TODO: Calculate change from previous period
					changeType: "increase",
					icon: "Building2",
					color: "primary",
				},
				{
					title: "Monthly Revenue",
					value: formatCurrency(monthlyRevenue, { showAbbreviation: true }),
					change: "0%", // TODO: Calculate change from previous period
					changeType: "increase",
					icon: "Currency",
					color: "success",
				},
				{
					title: "Pending Maintenance",
					value: pendingMaintenance.toString(),
					change: "0", // TODO: Calculate change from previous period
					changeType: "decrease",
					icon: "Wrench",
					color: "warning",
				},
				{
					title: "Total Properties",
					value: totalProperties.toString(),
					change: `${occupiedProperties} occupied`,
					changeType: "increase",
					icon: "Calendar",
					color: "error",
				},
			];
		} catch (error) {
			console.error("Error calculating KPI data:", error);
			return defaultKPIs;
		}
	}, [dashboardData]);

	const handlePropertyChange = (property: string): void => {
		setSelectedProperty(property);
		// In a real app, this would trigger data refetch
		
	};

	const handleDateRangeChange = (range: string): void => {
		setDateRange(range);
		// In a real app, this would trigger data refetch
	};

	const handleExport = () => {
		// Export KPI data as CSV
		const csvData = kpiData.map((kpi) => ({
			Property: selectedProperty,
			Metric: kpi.title,
			Value: kpi.value,
			Change: kpi.change,
		}));

		const csvContent = arrayToCsv(csvData, [
			"Property",
			"Metric",
			"Value",
			"Change",
		]);
		downloadCsv(csvContent, "dashboard-report");
	};

	// Show error state if dashboard query fails
	if (dashboardError) {
		console.error("Dashboard API error:", dashboardError);
	}

	// Show loading state
	if (isLoadingDashboard) {
		return (
			<div className="w-full">
				<div className="p-4 sm:p-6">
					<Loader
						size="lg"
						showText
						text="Loading dashboard..."
						className="min-h-[400px]"
					/>
				</div>
			</div>
		);
	}

	// Show error state
	if (dashboardError) {
		return (
			<div className="w-full">
				<div className="p-4 sm:p-6">
					<div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
						<div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center">
							<Icon name="AlertCircle" size={32} className="text-error" />
						</div>
						<h2 className="text-xl font-semibold text-foreground">
							Error Loading Dashboard
						</h2>
						<p className="text-muted-foreground text-center max-w-md">
							Failed to load dashboard data. Please try refreshing the page.
						</p>
						<Button
							onClick={() => window.location.reload()}
							iconName="RefreshCw"
							iconPosition="left"
						>
							Refresh Page
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="w-full">
			<div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
				{/* Page Header */}
				<div className="mb-4 sm:mb-6">
					<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0 gap-4">
						<div className="min-w-0 flex-1">
							<h1 className="text-2xl font-bold text-foreground truncate">
								Admin Dashboard
							</h1>
							<p className="text-muted-foreground mt-1 break-words">
								Welcome back! Here's what's happening with your properties
								today.
							</p>
						</div>
						<div className="text-sm text-muted-foreground flex-shrink-0 whitespace-nowrap">
							Last updated:{" "}
							{currentTime.toLocaleString("en-US", {
								month: "short",
								day: "numeric",
								hour: "2-digit",
								minute: "2-digit",
							})}
						</div>
					</div>
				</div>
			</div>

			{/* Property Selector and Filters */}
			<PropertySelector
				onPropertyChange={handlePropertyChange}
				onDateRangeChange={handleDateRangeChange}
				onExport={handleExport}
			/>

			{/* KPI Cards */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
				{kpiData.map((kpi, index) => (
					<KPICard
						key={index}
						title={kpi.title}
						value={kpi.value}
						change={kpi.change}
						changeType={kpi.changeType}
						icon={kpi.icon}
						color={kpi.color}
					/>
				))}
			</div>

			{/* Main Content Grid */}
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
				{/* Recent Activities */}
				<div className="h-[400px]">
					<RecentActivities />
				</div>

				{/* Quick Actions */}
				<div className="h-[400px]">
					<QuickActions />
				</div>

				{/* Payment Summary */}
				<div className="h-[400px]">
					<PaymentSummary />
				</div>
			</div>

			{/* Maintenance Overview - Full Width */}
			<div className="mb-8">
				<MaintenanceChart />
			</div>
		</div>
	);
};

export default AdminDashboard;
